import { FC } from 'react';

import { AxisBottom, AxisLeft } from '@visx/axis';
import { Grid } from '@visx/grid';
import { Group } from '@visx/group';
import { LegendOrdinal, LegendItem, LegendLabel } from '@visx/legend';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { Bar, Line } from '@visx/shape';
import { Text as SvgText } from '@visx/text';
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { NumberValue } from 'd3-scale';

import { DynamicPriceRecord } from '@dc-be/client/types.gen';
import { Box, Stack, Text } from '@sparky';

import { TooltipArrow } from './TooltipArrow';

const margin = { top: 20, bottom: 20, left: 72, right: 0 };

export type LegendItemLabel = {
  label: string;
  color: string;
};

export type XAxis = {
  category: string;
  data: string[];
};

export type YAxis = {
  category: string;
  label: string;
  min: number;
  max: number;
};

let tooltipTimeout: number;

/**
 * Renders a dynamic pricing bar graph along horizontal and vertical axes.
 * @param width Width of the graph
 * @param height Height of the graph
 * @param records Array of records
 * @param xAxis Category and indices for the x-axis
 * @param yAxis Category and indices for the y-axis
 * @param legend Labels for the legend of the graph
 * @param average Optional label to show the average of all records
 * @constructor
 */
export const AdvancedBarGraph: FC<
  React.PropsWithChildren<{
    width: number;
    height: number;
    records: DynamicPriceRecord[];
    xAxis: XAxis;
    yAxis: YAxis;
    legend?: LegendItemLabel[];
    average?: number;
    noConnection?: boolean;
    noConnectionLabel: string;
    averagePriceLabel: string;
  }>
> = ({ width, height, records, xAxis, yAxis, legend, average, noConnection, noConnectionLabel, averagePriceLabel }) => {
  /**
   *  Bounds
   */
  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom - 40;

  /**
   *  Axes
   */
  const xScale = scaleBand({
    domain: xAxis.data,
    range: [0, xMax],
  });

  const yScale = scaleLinear({
    domain: [yAxis.min, yAxis.max],
    range: [yMax, 0],
  });

  /**
   *  Legend
   */
  const legendScale = scaleOrdinal({
    domain: legend?.map(item => item.label),
    range: legend?.map(item => item.color),
  });

  /**
   *  Tooltip
   */
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, hideTooltip, showTooltip } =
    useTooltip<DynamicPriceRecord>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
  });

  return (
    <div style={{ position: 'relative', textAlign: 'center' }}>
      <svg width={width} height={height} ref={containerRef}>
        <Group left={margin.left} top={margin.top}>
          <Grid
            xScale={xScale}
            yScale={yScale}
            width={xMax}
            height={yMax}
            stroke="black"
            strokeOpacity={0.1}
            columnLineStyle={{ display: 'none' }}
          />

          {!noConnection &&
            records.map(record => {
              const barWidth = xScale.bandwidth();
              const barHeight = Math.abs(
                yScale(record[yAxis.category as keyof DynamicPriceRecord] as NumberValue) - yScale(0),
              );
              const barX = xScale(record[xAxis.category as keyof DynamicPriceRecord] + '') ?? 0;
              const barY = yScale(Math.max(record[yAxis.category as keyof DynamicPriceRecord] as number, 0));

              const barId = `bar-${record[xAxis.category as keyof DynamicPriceRecord]}`;

              const barColor =
                (record[yAxis.category as keyof DynamicPriceRecord] as number) > 0 ? '#7EC389' : '#FDE8B6';
              const barRoundedOffset = (record[yAxis.category as keyof DynamicPriceRecord] as number) > 0 ? 5 : 0;

              return (
                <>
                  <Bar
                    key={barId + '-small'}
                    x={barX + barWidth / 2 - 18}
                    y={barY + barRoundedOffset}
                    width={36}
                    height={barHeight - 5}
                    fill={barColor}
                  />
                  <Bar
                    key={barId}
                    rx={5}
                    x={barX + barWidth / 2 - 18}
                    y={barY}
                    width={36}
                    height={barHeight}
                    fill={barColor}
                    onMouseLeave={() => {
                      tooltipTimeout = window.setTimeout(() => {
                        hideTooltip();
                      }, 300);
                    }}
                    onMouseMove={e => {
                      if (tooltipTimeout) clearTimeout(tooltipTimeout);
                      const top = barY + margin.top / 2;
                      const left = (barWidth - 36) / 2 + margin.left + barX - 9;

                      showTooltip({
                        tooltipData: record,
                        tooltipTop: top,
                        tooltipLeft: left,
                      });
                    }}
                  />
                </>
              );
            })}

          {!noConnection && average && (
            <Line
              stroke="#3E798D"
              strokeDasharray="4"
              from={{ x: 0, y: yScale(average) }}
              to={{ x: xMax, y: yScale(average) }}
            />
          )}

          {noConnection && (
            <SvgText fontSize={24} fontWeight={700} lineHeight={28} fill="#D21242" x={xMax / 2 - 32 - 225} y={yMax / 2}>
              {noConnectionLabel}
            </SvgText>
          )}

          <AxisLeft
            scale={yScale}
            label={yAxis.label}
            left={-16}
            hideAxisLine
            hideTicks
            labelProps={{
              fontSize: 12,
              fontWeight: 500,
            }}
            tickLabelProps={() => ({
              fontSize: 14,
              textAnchor: 'end',
              verticalAnchor: 'middle',
            })}
          />

          <AxisBottom
            scale={xScale}
            top={yMax + 16}
            hideAxisLine
            hideTicks
            tickValues={xAxis.data}
            tickLabelProps={() => ({
              fontSize: 14,
              textAnchor: 'middle',
            })}
          />
        </Group>
      </svg>

      {!!legend?.length && (
        <LegendOrdinal scale={legendScale} shape={'circle'} direction="row" labelMargin="0 15px 0 0">
          {labels => (
            <Stack direction={'row'} gap={4} alignX={'center'}>
              {labels?.map((item, i) => (
                <LegendItem key={`legend-quantile-${i}`}>
                  <svg width={16} height={16}>
                    <circle fill={item.value} r={8} cx={8} cy={8} />
                  </svg>
                  <LegendLabel margin="0 10px">
                    <Text size="BodyXS" weight={'bold'}>
                      {item.text}
                    </Text>
                  </LegendLabel>
                </LegendItem>
              ))}

              {average && (
                <LegendItem>
                  <svg width={16} height={16}>
                    <circle fill={'#F3F3F3'} r={8} cx={8} cy={8} />
                    <line stroke={'#3E798D'} x1="0" y1="8" x2="16" y2="8" strokeDasharray="2 4 4 4 2" />
                  </svg>
                  <LegendLabel margin="0 10px">
                    <Text size="BodyXS" weight={'bold'}>
                      {averagePriceLabel}
                    </Text>
                  </LegendLabel>
                </LegendItem>
              )}
            </Stack>
          )}
        </LegendOrdinal>
      )}

      {tooltipOpen && tooltipData && (
        <TooltipInPortal offsetTop={-52} top={tooltipTop} left={tooltipLeft} unstyled applyPositionStyle>
          <Box padding="2" backgroundColor="backgroundDark" borderRadius="s">
            <Text size="BodyXS" weight={'bold'} color="textInverted">
              {tooltipData[xAxis.category as keyof DynamicPriceRecord]}
            </Text>
            <Text size="BodyXS" weight={'bold'} color="textInverted">
              {(tooltipData[yAxis.category as keyof DynamicPriceRecord] as number)?.toFixed(2) + ' € /MWh'}
            </Text>
          </Box>
          <TooltipArrow style={{ marginLeft: '12px' }} />
        </TooltipInPortal>
      )}
    </div>
  );
};
